import { useState } from 'react'
import { normalizeCode, normalizePhone } from '../../../lib/otp'
import {
  adminKeyStore,
  adminLogin,
  listLessons,
  otpRequest,
  otpVerify,
} from '../../../services/admin'
import { passwordLoginError, signInErrorMessage } from '../signInError'

// ─── Key entry ────────────────────────────────────────────────────────────────

export function KeyEntry({ onKey }) {
  const [mode, setMode] = useState('password') // 'password' | 'key' | 'phone'
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [val, setVal] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  function switchMode(next) { setMode(next); setErr(''); setCodeSent(false) }

  async function submitPassword(e) {
    e.preventDefault()
    const name = username.trim()
    if (!name || !password) return
    setBusy(true); setErr('')
    try {
      await adminLogin(name, password)
      onKey(await listLessons())
    } catch (ex) {
      setErr(passwordLoginError(ex))
      setPassword('')
    } finally { setBusy(false) }
  }

  async function submitKey(e) {
    e.preventDefault()
    const k = val.trim()
    if (!k) return
    setBusy(true); setErr('')
    try {
      adminKeyStore.set(k)
      onKey(await listLessons())
    } catch (ex) {
      adminKeyStore.clear()
      setErr(ex?.response?.status === 401 ? 'کلید نادرست است.' : signInErrorMessage(ex))
    } finally { setBusy(false) }
  }

  // V1-p: an admin types on a Persian keyboard like anyone else — the
  // number and the code go to the backend in Latin digits, E.164 for the
  // phone, whatever was typed.
  const phoneE164 = normalizePhone(phone)
  const codeDigits = normalizeCode(code)

  async function requestCode(e) {
    e.preventDefault()
    setBusy(true); setErr('')
    try { await otpRequest(phoneE164); setCodeSent(true) }
    catch (ex) { setErr(signInErrorMessage(ex)) }
    finally { setBusy(false) }
  }

  async function verifyCode(e) {
    e.preventDefault()
    setBusy(true); setErr('')
    try {
      await otpVerify(phoneE164, codeDigits)
      onKey(await listLessons())
    } catch (ex) { setErr(signInErrorMessage(ex)) }
    finally { setBusy(false) }
  }

  const FI = 'mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

  return (
    <div data-admin className="flex min-h-dvh items-center justify-center bg-slate-50">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-md">
        <div className="mb-6 flex items-center gap-2">
          <span className="text-2xl">🦜</span>
          <h1 className="text-xl font-bold text-slate-800">پنل ادمین</h1>
        </div>

        <div className="mb-5 flex rounded-lg bg-slate-100 p-1 text-sm">
          <button type="button" onClick={() => switchMode('password')}
            className={`flex-1 rounded-md py-1.5 transition-colors ${mode === 'password' ? 'bg-white font-medium text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            نام کاربری
          </button>
          <button type="button" onClick={() => switchMode('key')}
            className={`flex-1 rounded-md py-1.5 transition-colors ${mode === 'key' ? 'bg-white font-medium text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            کلید API
          </button>
          <button type="button" onClick={() => switchMode('phone')}
            className={`flex-1 rounded-md py-1.5 transition-colors ${mode === 'phone' ? 'bg-white font-medium text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            شماره تلفن
          </button>
        </div>

        {mode === 'password' && (
          <form onSubmit={submitPassword}>
            <label htmlFor="admin-username" className="mb-1 block text-sm font-medium text-slate-600">نام کاربری</label>
            <input
              id="admin-username" name="username" type="text" value={username} onChange={e => setUsername(e.target.value)}
              dir="ltr" className={FI} autoFocus autoComplete="username"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
            />
            <label htmlFor="admin-password" className="mb-1 mt-1 block text-sm font-medium text-slate-600">رمز عبور</label>
            <input
              id="admin-password" name="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              dir="ltr" className={FI} autoComplete="current-password"
            />
            {err && <p role="alert" className="mt-1 text-xs text-red-600">{err}</p>}
            <button type="submit" disabled={busy || !username.trim() || !password}
              className="mt-4 w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
              {busy ? 'در حال بررسی…' : 'ورود'}
            </button>
          </form>
        )}

        {mode === 'key' && (
          <form onSubmit={submitKey}>
            <label className="mb-1 block text-sm font-medium text-slate-600">کلید API ادمین</label>
            <input
              type="password" value={val} onChange={e => setVal(e.target.value)}
              placeholder="X-API-Key" className={FI}
              autoFocus autoComplete="current-password"
            />
            {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
            <button type="submit" disabled={busy || !val.trim()}
              className="mt-4 w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
              {busy ? 'در حال بررسی…' : 'ورود'}
            </button>
          </form>
        )}

        {mode === 'phone' && !codeSent && (
          <form onSubmit={requestCode}>
            <label className="mb-1 block text-sm font-medium text-slate-600">شماره تلفن</label>
            <input
              type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+989121234567" dir="ltr" className={FI}
              autoFocus
            />
            {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
            <button type="submit" disabled={busy || !phoneE164}
              className="mt-4 w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
              {busy ? 'در حال ارسال…' : 'ارسال کد'}
            </button>
          </form>
        )}

        {mode === 'phone' && codeSent && (
          <form onSubmit={verifyCode}>
            <label className="mb-1 block text-sm font-medium text-slate-600">کد ارسال‌شده به <span dir="ltr">{phoneE164}</span></label>
            <input
              type="text" inputMode="numeric" value={code} onChange={e => setCode(e.target.value)}
              placeholder="123456" dir="ltr" className={FI}
              autoFocus
            />
            {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
            <button type="submit" disabled={busy || !codeDigits}
              className="mt-4 w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
              {busy ? 'در حال بررسی…' : 'ورود'}
            </button>
            <button type="button" onClick={() => { setCodeSent(false); setCode(''); setErr('') }}
              className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600">
              تغییر شماره
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
